import { format, parseISO, isValid } from 'date-fns';
import { ptBR, Locale } from 'date-fns/locale';

/** Extrai YYYY-MM-DD de strings ISO, Postgres (espaço) ou Date. */
export function extractDateOnly(date: Date | string | null | undefined): string | null {
  if (!date) return null;
  if (date instanceof Date) {
    if (!isValid(date)) return null;
    return format(date, 'yyyy-MM-dd');
  }
  if (typeof date === 'string') {
    const trimmed = date.trim();
    const match = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  }
  return null;
}

export function formatCoverDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  locale: Locale = ptBR
): string {
  const start = extractDateOnly(startDate);
  const end = extractDateOnly(endDate);
  if (!start && !end) return '';
  if (start && end) {
    return `${formatSafeDate(start, 'dd/MM/yyyy', locale)} - ${formatSafeDate(end, 'dd/MM/yyyy', locale)}`;
  }
  if (start) return formatSafeDate(start, 'dd/MM/yyyy', locale);
  return formatSafeDate(end, 'dd/MM/yyyy', locale);
}

export function formatSafeDate(date: Date | string | null | undefined | any, formatStr: string, locale: Locale = ptBR): string {
  try {
    if (!date) return '';
    
    let dateObj: Date;
    
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      // Check if the string is in ISO format (YYYY-MM-DD)
      const isoDatePattern = /^\d{4}-\d{2}-\d{2}(T|\s|$)/;
      
      if (isoDatePattern.test(date)) {
        const datePart = extractDateOnly(date) || date.split('T')[0];
        
        // Create date at noon UTC to prevent timezone issues
        dateObj = new Date(`${datePart}T12:00:00Z`);
      } else {
        // For other string formats, try to create a date directly
        dateObj = new Date(date);
        
        // If the date is invalid, try parseISO as a fallback
        if (isNaN(dateObj.getTime())) {
          try {
            dateObj = parseISO(date);
          } catch (error) {
            return '';
          }
        }
      }
    } else if (typeof date === 'object' && date.hasOwnProperty('toDate')) {
      // Handle Firestore Timestamp or similar objects
      dateObj = date.toDate();
    } else if (typeof date === 'object' && date.hasOwnProperty('seconds')) {
      // Handle Unix timestamp objects
      dateObj = new Date(date.seconds * 1000);
    } else if (typeof date === 'number') {
      // Handle Unix timestamps
      dateObj = new Date(date);
    } else {
      return '';
    }

    // Verifica se a data é válida
    if (!isValid(dateObj)) {
      return '';
    }

    return format(dateObj, formatStr, { locale });
  } catch (error) {
    return '';
  }
}

export function parseAndFormatDate(dateStr: string | null | undefined, formatStr: string, locale: Locale = ptBR): string {
  try {
    if (!dateStr) return '';
    
    // Handle ISO dates specifically to prevent timezone issues
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}(T|$)/;
    if (isoDatePattern.test(dateStr)) {
      // Get only the date part if it includes time
      const datePart = dateStr.split('T')[0];
      
      // Create date at noon UTC to prevent timezone issues
      const date = new Date(`${datePart}T12:00:00Z`);
      if (!isValid(date)) return '';
      return format(date, formatStr, { locale });
    }
    
    const date = parseISO(dateStr);
    if (!isValid(date)) return '';
    return format(date, formatStr, { locale });
  } catch (error) {
    return '';
  }
} 